/**
 * File: paymentConstants.ts
 * Path: packages/config/src/pricing/paymentConstants.ts
 * Description: Unified SSOT for all payment definitions.
 * Innovation pricing is now a strict extension of the PAYMENT_REGISTRY.
 */
import { PaymentOption, PaymentPlanId, PaymentPlanMeta } from "./pricingTypes";

// ==========================================
// 1. MASTER PAYMENT REGISTRY
// ==========================================

/**
 * Months a package is paid over. Each payment is the bundle spread over this
 * term plus the care plan; from the month after, the client pays only care.
 * Plan markups are internal: clients only ever see each option's fixed total
 * (deferred-price sale, no visible "cash + X%", per the Sharia rules).
 */
export const PACKAGE_TERM_MONTHS = 12;

export const PAYMENT_REGISTRY = {
  // --- Unified Options ---
  once_off: {
    id: "once_off",
    // No discount: the "save 10%" this used to promise was never applied to
    // any total (markup has always been 0), so the claim is gone.
    label: "Pay Upfront",
    description: "Pay the full amount in one payment, at the start.",
    markup: 0,
    instalments: 1,
  },
  split_50_50: {
    id: "split_50_50",
    label: "The Fair Split",
    description:
      "Balanced and fair. 50% down to start the work, and the last 50% only when your new website is live.",
    markup: 0,
    instalments: 2,
  },
  phase_by_phase: {
    id: "phase_by_phase",
    label: "Pay As We Build",
    description:
      "Keep your cash moving smoothly. Pay in small, easy chunks every time we finish a new part of the project.",
    markup: 0,
    instalments: null,
  },
  split_6_month: {
    id: "split_6_month",
    label: "6-Month Plan",
    description:
      "Six equal monthly payments, at one fixed total agreed before you sign.",
    markup: 0.06,
    instalments: 6,
  },
  split_12_month: {
    id: "split_12_month",
    label: "12-Month Plan",
    description:
      "Twelve equal monthly payments, at one fixed total agreed before you sign.",
    markup: 0.12,
    instalments: 12,
  },

  // --- Innovation Options ---
  sprint: {
    id: "sprint",
    label: "Step-by-Step Building",
    description:
      "Great for big custom projects. A fixed build price, paid stage by stage as each part of your tools is built and launched. The price is fixed once the Blueprint is done.",
    markup: 0,
    instalments: null,
  },
  equity_retainer: {
    id: "equity_retainer",
    label: "The True Partnership",
    description:
      "We lower your monthly investment in return for a small share of the business. If your till rings, we get paid.",
    markup: 0,
    instalments: null,
  },
} as const satisfies Record<string, PaymentOption | PaymentPlanMeta>;

// ==========================================
// 2. INNOVATION PRICING (Registry Extension)
// ==========================================

/**
 * Strict extension of the registry for Innovation Tier logic.
 * No individual constant exports; tools will use this object directly.
 */
export const INNOVATION_PRICING = {
  forge: {
    ...PAYMENT_REGISTRY.sprint,
    // Same fee as the App Blueprint & Prototype service (serviceContent test holds them equal).
    blueprint_investment: 5000,
    sprint_investment: 11500,
    // Fixed stages of about two weeks each, as the FORGE copy and the site say.
    // The per-stage figure is only the estimate unit until the Blueprint
    // fixes the build price.
    sprint_weeks: 2,
  },
  venture: {
    ...PAYMENT_REGISTRY.equity_retainer,
    retainer_investment: 3000,
    partnership_months: 18,
  },
} as const;

// ==========================================
// 3. PACKAGE / MARKETING ARRAYS
// ==========================================

export const STANDARD_PAYMENT_OPTIONS: readonly PaymentOption[] = [
  PAYMENT_REGISTRY.once_off,
  PAYMENT_REGISTRY.split_50_50,
  PAYMENT_REGISTRY.phase_by_phase,
];

export const FORGE_PAYMENT_OPTIONS: readonly PaymentOption[] = [
  PAYMENT_REGISTRY.sprint,
  PAYMENT_REGISTRY.phase_by_phase,
];

export const VENTURE_PAYMENT_OPTIONS: readonly PaymentOption[] = [
  PAYMENT_REGISTRY.equity_retainer,
];

// ==========================================
// 4. CART CHECKOUT CONFIGURATION
// ==========================================

export const PAYMENT_PLANS: Record<PaymentPlanId, PaymentPlanMeta> = {
  once_off: PAYMENT_REGISTRY.once_off,
  split_50_50: PAYMENT_REGISTRY.split_50_50,
  phase_by_phase: PAYMENT_REGISTRY.phase_by_phase,
  split_6_month: PAYMENT_REGISTRY.split_6_month,
  split_12_month: PAYMENT_REGISTRY.split_12_month,
};

export const PAYMENT_PLAN_ORDER: PaymentPlanId[] = [
  "once_off",
  "split_50_50",
  "phase_by_phase",
  "split_6_month",
  "split_12_month",
];
